import { AuthorizedContent } from '@frontegg/react';
import React, { FC, SyntheticEvent, useState, useEffect, useRef } from 'react';
import { useDashboardMode } from '../../../contexts/dashboardModeContext';
import RolesType from '../../../utils/enum/RolesType';
import { FilterResults } from '../../Modals/FilterModal/filterModalInterface';
import { DashboardFromApi, UpdateDashboardProps } from '../dashboardInterfaces';
import { FilterPanelStyled, FilterPanelFooterStyled } from '../styled';
import SelectedFilterSummary from '../TemporalScope/selectedFilterSummary';
import AddFilterButton from './AddFilterButton/AddFilterButton';
import FilterSelect from './FilterSelect/filterSelect';
import ButtonResetFilters from './ResetFilters/buttonResetFilters';
import { AccordionStyled, AccordionSummaryStyled, AccordionDetailsStyled, FilterBarStyled, FilterElementStyled, FilterImageStyled } from './styled';

interface FilterBarProps {
    updateDashboard: (filters: FilterResults) => void;
    setUpdatedDasboard: React.Dispatch<React.SetStateAction<UpdateDashboardProps| undefined>>;
    alreadySelectedFilter: UpdateDashboardProps['dashboardFilterConfig'] | undefined;
    filters: DashboardFromApi['dashboardFilterConfig'];
    handleSelectClauseFilter: ([filterId, modalityId]: [string, Array<string> | null]) => void;
    handleSelectClauseFilterLabel: ([filterLabel, modalitiesLabel]: [string, string | null]) => void;
    handleResetClauseFilter: (resetFilters: number) => void;
    resetFilters: number;
    removeDashboardFilter: (filterId: string) => void;
    selectedClauseFilterLabel?: any;
}

const FilterBar: FC<FilterBarProps> = ({ updateDashboard, setUpdatedDasboard, alreadySelectedFilter, filters, handleSelectClauseFilter, handleSelectClauseFilterLabel, handleResetClauseFilter, removeDashboardFilter, resetFilters, selectedClauseFilterLabel }) => {
    const { isCreationMode } = useDashboardMode();
    const [lockSelectedFilter, setLockSelectedFilter] = useState<string[]>([]);
    const [expanded, setExpanded] = useState<string | false>(false);
    const accordionRef = useRef<HTMLDivElement>(null);

    const handleChange = (panel: string) => (event: SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // Check if click is inside a MUI Select menu (portaled element)
            const isInsideMenu = target.closest('.MuiMenu-root, .MuiPopover-root, [role="presentation"]');

            if (accordionRef.current && !accordionRef.current.contains(target) && !isInsideMenu && expanded === 'filter') {
                setExpanded(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [expanded]);

    return (
        <AccordionStyled ref={accordionRef} expanded={expanded === 'filter'} height={expanded ? '54px' : '54px'} onChange={handleChange('filter')}>
            <AccordionSummaryStyled
                $hasSelection={selectedClauseFilterLabel.length > 0}
                aria-controls="filter-content"
                id="filter-header"
            >
                <FilterImageStyled />
                { selectedClauseFilterLabel.length > 0
            && selectedClauseFilterLabel.map((filterLabel: any) => (
                <SelectedFilterSummary
                    key={filterLabel.filterLabel}
                    selectedValuesLabel={filterLabel.modalitiesLabel}
                    varTitle={filterLabel.filterLabel}
                />
            ))}
            </AccordionSummaryStyled>
            <AccordionDetailsStyled>
                <FilterBarStyled>
                    <FilterPanelStyled>
                        { (filters.length > 0 || isCreationMode)
                    && (
                        <FilterElementStyled>
                            {filters.map((filter) => (
                                <FilterSelect
                                    key={Object.keys(filter.filter)[0]}
                                    filter={filter}
                                    handleSelectClauseFilter={handleSelectClauseFilter}
                                    handleSelectClauseFilterLabel={handleSelectClauseFilterLabel}
                                    lockSelectedFilter={lockSelectedFilter}
                                    lockedFilters={filter.lockedModalities ? filter.lockedModalities.toString().split(',') : []}
                                    removeDashboardFilter={removeDashboardFilter}
                                    resetFilters={resetFilters}
                                    setLockSelectedFilter={setLockSelectedFilter}
                                />
                            ))}
                            {isCreationMode && (
                                <AuthorizedContent requiredRoles={[RolesType.ADMIN, RolesType.ADMIN_MOAI]}>
                                    <AddFilterButton
                                        alreadySelectedFilter={alreadySelectedFilter}
                                        setUpdatedDasboard={setUpdatedDasboard}
                                        updateDashboard={updateDashboard}
                                    />
                                </AuthorizedContent>
                            )}
                        </FilterElementStyled>
                    )}
                        <FilterPanelFooterStyled>
                            <ButtonResetFilters
                                handleResetClauseFilter={handleResetClauseFilter}
                            />
                        </FilterPanelFooterStyled>
                    </FilterPanelStyled>
                </FilterBarStyled>
            </AccordionDetailsStyled>
        </AccordionStyled>
    );
};

export default FilterBar;
